import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import * as React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@payment-plan/ui';
import { useAuth } from '../../../auth/auth-context';
import { readInmobiliariaActiva, writeInmobiliariaActiva } from '../../../auth/session-storage';
import { apiClient } from '../../../lib/api-client';
import { setInmobiliariaActiva } from '../../../lib/inmobiliaria-activa';
import { can } from '../../../lib/permissions';
import type { Company, Paginated } from '../../../types/admin';

/**
 * fix-60 · ADR-018: inmobiliaria sobre la que trabaja el operador de la plataforma en Proyectos,
 * Catálogo, Asesores y Usuarios. Solo con `plataforma.gestionar_inmobiliarias` se puede cambiar; sin
 * él, siempre es la de la sesión y el `sessionStorage` se ignora (TC-47). El valor viaja al API como
 * `X-Inmobiliaria-Id` solo en las llamadas administrativas (`lib/inmobiliaria-activa`).
 */
export const PERMISO_GESTION_PLATAFORMA = 'plataforma.gestionar_inmobiliarias';

interface InmobiliariaActivaValue {
  /** Inmobiliaria efectiva (la elegida o la de la sesión); `null` sin sesión. */
  inmobiliariaId: string | null;
  /** true si trabaja sobre una inmobiliaria distinta de la de su sesión. */
  esOtra: boolean;
  puedeCambiar: boolean;
  cambiar: (id: string) => void;
}

const SIN_PROVEEDOR: InmobiliariaActivaValue = {
  inmobiliariaId: null,
  esOtra: false,
  puedeCambiar: false,
  cambiar: () => undefined,
};

const InmobiliariaActivaContext = React.createContext<InmobiliariaActivaValue>(SIN_PROVEEDOR);

export function InmobiliariaActivaProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user } = useAuth();
  const puedeCambiar = can(user?.permissions, PERMISO_GESTION_PLATAFORMA);
  const propia = user?.inmobiliariaId ?? null;
  // Se lee siempre; solo cuenta con el permiso (la sesión puede cargar después del primer render).
  const [elegida, setElegida] = React.useState<string | null>(() => readInmobiliariaActiva());
  const destino = puedeCambiar && elegida && elegida !== propia ? elegida : null;

  // Sincrónico (idempotente): las consultas del primer render ya salen con el encabezado correcto.
  setInmobiliariaActiva(destino);
  React.useEffect(() => () => setInmobiliariaActiva(null), []);

  const cambiar = React.useCallback(
    (id: string) => {
      const valor = id === propia ? null : id;
      setElegida(valor);
      writeInmobiliariaActiva(valor);
    },
    [propia],
  );

  const value = React.useMemo<InmobiliariaActivaValue>(
    () => ({ inmobiliariaId: destino ?? propia, esOtra: destino !== null, puedeCambiar, cambiar }),
    [cambiar, destino, propia, puedeCambiar],
  );

  return <InmobiliariaActivaContext.Provider value={value}>{children}</InmobiliariaActivaContext.Provider>;
}

export function useInmobiliariaActiva(): InmobiliariaActivaValue {
  return React.useContext(InmobiliariaActivaContext);
}

/** Segmento de las claves de React Query: la caché nunca mezcla datos de dos inmobiliarias. */
export function useClaveInmobiliaria(): string {
  return useInmobiliariaActiva().inmobiliariaId ?? 'sesion';
}

/** Selector visible SOLO con el permiso de plataforma (Web 01/03). */
export function InmobiliariaSelector(): React.JSX.Element | null {
  const { inmobiliariaId, esOtra, puedeCambiar, cambiar } = useInmobiliariaActiva();
  const { data } = useQuery<Paginated<Company>>({
    queryKey: ['companies', 'selector'],
    queryFn: () => apiClient.companies.list({ page: 1, pageSize: 100 }),
    enabled: puedeCambiar,
  });

  if (!puedeCambiar) {
    return null;
  }
  const activas = (data?.data ?? []).filter((c) => c.activa || c.id === inmobiliariaId);
  const actual = activas.find((c) => c.id === inmobiliariaId);

  return (
    <section
      aria-label="Inmobiliaria de trabajo"
      className="flex flex-col gap-2 rounded-xl border border-border/70 bg-surface-fade px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-center gap-2 text-sm">
        <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <label htmlFor="inmobiliaria-activa" className="font-medium text-foreground">
          Inmobiliaria
        </label>
        {esOtra && actual && (
          <span className="text-muted-foreground">· Estás administrando {actual.nombre}</span>
        )}
      </div>
      <div className="w-full sm:w-80">
        <Select value={inmobiliariaId ?? undefined} onValueChange={cambiar}>
          <SelectTrigger id="inmobiliaria-activa" aria-label="Inmobiliaria de trabajo" className="h-11">
            <SelectValue placeholder="Selecciona una inmobiliaria" />
          </SelectTrigger>
          <SelectContent>
            {activas.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </section>
  );
}
